<template>
  <Teleport :to="props.teleportTarget">
    <Transition name="book-share-mobile-fade">
      <!-- 海报打开时隐藏底层面板，避免先关海报再关分享、需点两下 -->
      <div
        v-if="props.visible && !posterVisible"
        class="book-share-mobile-backdrop"
        @click.self="emit('close')"
      >
        <!-- 面板区域拦截冒泡，避免触发阅读器翻页 -->
        <div
          class="book-share-mobile-sheet"
          @click.stop
          @pointerdown.stop
          @pointerup.stop
          @touchstart.stop.passive
          @touchend.stop
        >
          <div class="book-share-mobile-grid">
            <button
              v-for="item in shareItems"
              :key="item.key"
              class="book-share-mobile-item"
              type="button"
              @click.stop="handleItemClick(item)"
            >
              <img
                class="book-share-mobile-icon"
                :src="item.icon"
                alt=""
                aria-hidden="true"
              />
              <span class="book-share-mobile-label">{{ item.label }}</span>
            </button>
          </div>

          <button class="book-share-mobile-cancel" type="button" @click.stop="emit('close')">
            取消
          </button>
        </div>
      </div>
    </Transition>

    <Transition name="book-share-mobile-fade">
      <div
        v-if="guideVisible"
        class="book-share-mobile-guide"
        role="dialog"
        aria-live="polite"
        @click.self="guideVisible = false"
      >
        <div class="book-share-mobile-guide-card">
          <p class="book-share-mobile-guide-text">{{ guideText }}</p>
          <button class="book-share-mobile-guide-btn" type="button" @click="guideVisible = false">
            知道了
          </button>
        </div>
      </div>
    </Transition>

    <Transition name="book-share-mobile-fade">
      <div
        v-if="qrVisible"
        class="book-share-mobile-qr"
        role="dialog"
        aria-label="绘本预览二维码"
        @click.self="qrVisible = false"
      >
        <div class="book-share-mobile-qr-card">
          <div class="book-share-mobile-qr-title">扫一扫二维码预览</div>
          <img
            v-if="shareQrUrl"
            class="book-share-mobile-qr-image"
            :src="shareQrUrl"
            alt="绘本预览二维码"
          />
          <button class="book-share-mobile-guide-btn" type="button" @click="qrVisible = false">
            关闭
          </button>
        </div>
      </div>
    </Transition>

    <Transition name="book-share-mobile-fade">
      <div v-if="copied" class="book-share-mobile-toast" role="status">链接已复制</div>
    </Transition>

    <BookSharePoster
      :visible="posterVisible"
      :title="props.title"
      :cover-url="posterCoverUrl"
      :qr-code-url="shareQrUrl"
      @close="handlePosterClose"
      @error="showGuide('海报生成失败，请稍后重试')"
    />
  </Teleport>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import shareLinkIcon from '@/assets/images/book/share-mobile-link.svg?url';
  import sharePosterIcon from '@/assets/images/book/share-mobile-poster.svg?url';
  import shareQrcodeIcon from '@/assets/images/book/share-mobile-qrcode.svg?url';
  import shareWechatIcon from '@/assets/images/book/share-mobile-wechat.svg?url';
  import BookSharePoster from './BookSharePoster.vue';
  import { useShareQrcode } from '@/hooks/useShareQrcode';
  import type { ShareRuntimeWindow } from '@/types/share';
  import { sharePhoneWeChat } from '@/utils/analysisShare';
  import { getAbsolutePath } from '@/utils/projectUtil';
  import { useProjectStore } from '@/store/project';
  import { storeToRefs } from 'pinia';

  const props = withDefaults(
    defineProps<{
      visible: boolean;
      title: string;
      teleportTarget?: string | HTMLElement;
    }>(),
    {
      teleportTarget: 'body'
    }
  );

  const emit = defineEmits<{
    close: [];
  }>();

  const { baseUrl, book, shareUrl, shareCoverUrl } = storeToRefs(useProjectStore());
  const { dataUrl: shareQrUrl } = useShareQrcode(shareUrl);
  const posterCoverUrl = computed(() => {
    const coverUrl = shareCoverUrl.value || getBookCoverUrl();
    return normalizePosterAssetUrl(coverUrl);
  });
  const copied = ref(false);
  const guideVisible = ref(false);
  const guideText = ref('');
  const qrVisible = ref(false);
  const posterVisible = ref(false);
  let copiedTimer: number;

  const shareItems = [
    { key: 'wechat', label: '微信', icon: shareWechatIcon },
    { key: 'poster', label: '海报', icon: sharePosterIcon },
    { key: 'qrcode', label: '二维码', icon: shareQrcodeIcon },
    { key: 'link', label: '链接', icon: shareLinkIcon }
  ] as const;

  function normalizePosterAssetUrl(url?: string) {
    if (!url) return '';
    if (/^(data:|blob:|https?:\/\/)/i.test(url)) return url;

    try {
      return getAbsolutePath(baseUrl.value || window.location.href, url);
    } catch {
      return url;
    }
  }

  function getBookCoverUrl() {
    const pages = book.value.pages ?? [];
    const coverPage = pages.find((page) => page.type === 'cover') ?? pages[0];
    const imageAction = coverPage?.actions?.find((action) => action.element?.type === 'image');
    const content = imageAction?.element?.content;

    if (typeof content === 'string') return content;
    return imageAction?.element?.relativePath ?? '';
  }

  // 展示分享引导文案
  function showGuide(message: string) {
    guideText.value = message;
    guideVisible.value = true;
  }

  // 复制分享链接，优先使用 Clipboard API，失败时回退到 textarea
  async function copyShareUrl(): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(shareUrl.value);
        return true;
      } catch {
        // 继续尝试 textarea 回退方案
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = shareUrl.value;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }

  // 展示“已复制”提示，并在短暂延迟后关闭分享面板
  function showCopiedToast() {
    copied.value = true;
    if (copiedTimer !== undefined) window.clearTimeout(copiedTimer);
    copiedTimer = window.setTimeout(() => {
      copied.value = false;
      emit('close');
    }, 1200);
  }

  type RuntimeWindow = Window & ShareRuntimeWindow;

  // 获取分享运行时对象
  function getShareRuntimeWindow(): RuntimeWindow {
    return window as RuntimeWindow;
  }

  // 处理移动端分享项点击
  async function handleItemClick(item: (typeof shareItems)[number]) {
    switch (item.key) {
      case 'wechat':
        if (getShareRuntimeWindow().phoneSvgIcons?.shareToWeChat) {
          sharePhoneWeChat();
          emit('close');
          return;
        }
        showGuide('请点击右上角菜单「...」，分享给微信好友或朋友圈');
        return;
      case 'poster':
        // 打开海报；底层面板由 !posterVisible 隐藏，关闭海报时一并退出分享
        posterVisible.value = true;
        return;
      case 'qrcode':
        qrVisible.value = true;
        return;
      case 'link': {
        const success = await copyShareUrl();
        if (success) {
          showCopiedToast();
          return;
        }
        showGuide('复制失败，请长按链接手动复制：' + shareUrl);
        return;
      }
    }
  }

  /** 关闭海报并退出分享面板，一次点击清掉全部遮罩 */
  function handlePosterClose() {
    posterVisible.value = false;
    emit('close');
  }

  watch(
    () => props.visible,
    (visible) => {
      if (!visible) posterVisible.value = false;
    }
  );
</script>

<style scoped>
  .book-share-mobile-backdrop {
    position: fixed;
    inset: 0;
    z-index: 3100;
    display: flex;
    align-items: flex-end;
    background: rgba(0, 0, 0, 0.45);
  }

  .book-share-mobile-sheet {
    position: relative;
    z-index: 1;
    width: 100%;
    background: #f6f7f8;
    border-radius: 14px 14px 0 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    pointer-events: auto;
  }

  .book-share-mobile-grid {
    width: 100%;
    height: 129px;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(4, 56px);
    justify-content: space-between;
    gap: 0;
    padding: 25.5px 33px;
    background: #f6f7f8;
    border-radius: 14px 14px 0 0;
    box-sizing: border-box;
  }

  .book-share-mobile-item {
    width: 56px;
    height: 78px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 0;
    color: #333;
    background: transparent;
    border: 0;
    font-size: 11px;
    line-height: 14px;
    letter-spacing: -0.275px;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .book-share-mobile-icon {
    display: block;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    box-shadow:
      0 2px 4px rgba(0, 0, 0, 0.1),
      0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .book-share-mobile-item:active {
    opacity: 0.7;
  }

  .book-share-mobile-cancel {
    width: calc(100% - 10px);
    height: 38px;
    color: #333940;
    background: #fff;
    border: 0;
    border-radius: 10px;
    font-size: 14px;
    line-height: 22px;
    margin: 5px 5px 10px;
    font-weight: 500;
    cursor: pointer;
    touch-action: manipulation;
  }

  .book-share-mobile-cancel:active {
    background: #ebe7f3;
  }

  .book-share-mobile-guide,
  .book-share-mobile-qr {
    position: fixed;
    inset: 0;
    z-index: 3200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(0, 0, 0, 0.55);
    box-sizing: border-box;
  }

  .book-share-mobile-guide-card,
  .book-share-mobile-qr-card {
    width: min(100%, 320px);
    padding: 20px;
    color: #333940;
    text-align: center;
    background: #fff;
    border-radius: 12px;
    box-sizing: border-box;
  }

  .book-share-mobile-guide-text {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 22px;
    word-break: break-all;
  }

  .book-share-mobile-qr-title {
    margin-bottom: 12px;
    font-size: 16px;
    line-height: 24px;
  }

  .book-share-mobile-qr-image {
    display: block;
    width: 150px;
    height: 150px;
    margin: 0 auto 16px;
    border: 1px solid #eff0f7;
    border-radius: 6px;
    object-fit: contain;
  }

  .book-share-mobile-guide-btn {
    min-width: 96px;
    height: 40px;
    padding: 0 16px;
    color: #fff;
    background: #6033e1;
    border: 0;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
  }

  .book-share-mobile-toast {
    position: fixed;
    left: 50%;
    bottom: 120px;
    z-index: 3300;
    padding: 10px 16px;
    color: #fff;
    background: rgba(0, 0, 0, 0.78);
    border-radius: 8px;
    font-size: 14px;
    line-height: 22px;
    transform: translateX(-50%);
    pointer-events: none;
  }

  .book-share-mobile-fade-enter-active,
  .book-share-mobile-fade-leave-active {
    transition: opacity 200ms ease;
  }

  .book-share-mobile-fade-enter-from,
  .book-share-mobile-fade-leave-to {
    opacity: 0;
  }

  /* 底部弹层：进场从底部滑入，退场向底部滑出 */
  .book-share-mobile-fade-enter-active .book-share-mobile-sheet,
  .book-share-mobile-fade-leave-active .book-share-mobile-sheet {
    transition: transform 250ms ease;
  }

  .book-share-mobile-fade-enter-from .book-share-mobile-sheet,
  .book-share-mobile-fade-leave-to .book-share-mobile-sheet {
    transform: translateY(100%);
  }

  @media (prefers-reduced-motion: reduce) {
    .book-share-mobile-fade-enter-active,
    .book-share-mobile-fade-leave-active,
    .book-share-mobile-fade-enter-active .book-share-mobile-sheet,
    .book-share-mobile-fade-leave-active .book-share-mobile-sheet {
      transition: none;
    }
  }
</style>
